import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { withLogger } from '@/new-transport/_store/logger';
import { withBasicTransport } from '@/new-transport/_store/basic-transport.feature';
import { withLoadingFeature } from '@/_signal-store-features/loading.feature.store';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, filter, map, pipe, startWith, Subject, switchMap } from 'rxjs';
import { ReservationOfferTransport } from '@/new-transport/_models/transport-list.model';
import { computed, DestroyRef, inject } from '@angular/core';
import { TransportListApiService } from '../_api/transport-list.api.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { countryFromLocation } from '@/_utils/country-from-location';
import { CancellationStatuses, Status } from '@/common/enums/Status';
import { Location } from '@/reservation/reservation.model';

export type TransportListState = {
	offerTransport: ReservationOfferTransport[];
};

const transportListInitialState: TransportListState = {
	offerTransport: [],
};

const manualRefresh$ = new Subject<void>();

export const TransportListStore = signalStore(
	withState(transportListInitialState),
	withLogger('offerTransport'),
	withLoadingFeature({ offerTransportIsLoading: false }),
	withBasicTransport(),
	withComputed(({ offerTransport }) => ({
		offerTransportList: computed(() => {
			return offerTransport().flatMap((item) => {
				return item.slot
					.filter((value) => !CancellationStatuses.includes(value.status as Status))
					.map((slotItem) => ({
						...item,
						slot: slotItem,
						reservationSlotCount: item.slot.length,
						destination: countryFromLocation(item.product.location[0] as unknown as Location),
					}))
					.sort((a, b) => {
						const dateA = new Date(a.slot.stopDeparture?.['timeDeparture']);
						const dateB = new Date(b.slot.stopDeparture?.['timeDeparture']);
						return dateA.getTime() - dateB.getTime();
					});
			});
		}),
	})),
	withMethods((store, apiService = inject(TransportListApiService)) => ({
		fetchTransportList: rxMethod<{ transportsId: number[]; params?: string }>(
			pipe(
				switchMap(({ transportsId, params }) =>
					apiService.getOfferTransport(transportsId, params).pipe(
						tapResponse({
							next: (offerTransport) => {
								patchState(store, () => ({
									offerTransport,
								}));
							},
							error: () => {
								throw new Error('Wystąpił problem przy pobieraniu list');
							},
							finalize: () => {
								store.changeLoadingState('offerTransport', false);
							},
						}),
					),
				),
			),
		),
		triggerRefresh(): void {
			patchState(store, { offerTransport: [] });
			manualRefresh$.next();
		},
	})),
	withHooks({
		onInit({ transport, transportsToLink, fetchTransportList, isLinkedTransport }) {
			const destroyRef = inject(DestroyRef);

			const transport$ = toObservable(transport);
			const transportsToLink$ = toObservable(transportsToLink);

			const shouldFetch$ = combineLatest([
				transport$,
				transportsToLink$,
				manualRefresh$.pipe(startWith(void 0)),
			]).pipe(
				filter(([t, linked]) => !!t && (!isLinkedTransport() || linked.length > 0)),
				map(([t, linked]) => ({
					transportsId: [t.id, ...linked.map((l) => l.id)],
					params: 'limit=999',
				})),
			);

			shouldFetch$.pipe(takeUntilDestroyed(destroyRef)).subscribe((payload) => {
				fetchTransportList(payload);
			});
		},
	}),
);
